import { AnimatePresence, motion } from 'framer-motion';
import { Check, ChevronDown, TrendingUp, User, Users } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import MetricInfoTooltip from './MetricInfoTooltip';

interface SalesTrendDataPoint {
  month: string;
  newOps: number;
  contractsSigned: number;
  activeOps: number;
  isProjection?: boolean;
  isPartial?: boolean;
  actualData?: {
    newOps: number;
    contractsSigned: number;
    activeOps: number;
  };
}

interface SalesTrendLineChartProps {
  data: SalesTrendDataPoint[];
  title: string;
  info?: string;
  query?: string;
  rawRecords?: any[];
  salesReps?: string[];
}

interface TooltipData {
  month: string;
  newOps: number;
  contractsSigned: number;
  activeOps: number;
  x: number;
  y: number;
  isProjection?: boolean;
  isPartial?: boolean;
  actualData?: {
    newOps: number;
    contractsSigned: number;
    activeOps: number;
  };
}

export const SalesTrendLineChart: React.FC<SalesTrendLineChartProps> = ({
  data,
  title,
  info,
  query,
  rawRecords = [],
  salesReps = [],
}) => {
  const [hoveredPoint, setHoveredPoint] = useState<TooltipData | null>(null);
  const [selectedSalesRep, setSelectedSalesRep] = useState<string>('All Sales Reps');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter data by selected sales rep if needed
  let displayData = data;

  if (selectedSalesRep !== 'All Sales Reps' && rawRecords.length > 0) {
    // Filter records by selected sales rep
    const filteredRecords = rawRecords.filter((record: any) => {
      const ownerName = record['Owner.Name'] || record.Owner?.Name;

      return ownerName === selectedSalesRep;
    });

    // Recalculate monthly data for filtered records
    const monthlyData: Record<
      string,
      { newOps: number; contractsSigned: number; activeOps: number }
    > = {};

    // First, get all months from the original data to ensure we calculate activeOps for all months
    const allMonths = new Set<string>(data.filter(d => !d.isProjection).map(d => d.month));

    filteredRecords.forEach((record: any) => {
      // Count new ops created
      if (record.CreatedDate) {
        const createdDate = new Date(record.CreatedDate);
        const createdMonth = `${createdDate.getFullYear()}-${String(createdDate.getMonth() + 1).padStart(2, '0')}`;

        if (!monthlyData[createdMonth]) {
          monthlyData[createdMonth] = { newOps: 0, contractsSigned: 0, activeOps: 0 };
        }

        monthlyData[createdMonth].newOps++;
        allMonths.add(createdMonth);
      }

      // Count contracts signed
      if (record.EHR_Contract__c === 'Signed' && record.EHR_Contract_Signed_Date__c) {
        const signedDate = new Date(record.EHR_Contract_Signed_Date__c);
        const signedMonth = `${signedDate.getFullYear()}-${String(signedDate.getMonth() + 1).padStart(2, '0')}`;

        if (!monthlyData[signedMonth]) {
          monthlyData[signedMonth] = { newOps: 0, contractsSigned: 0, activeOps: 0 };
        }

        monthlyData[signedMonth].contractsSigned++;
        allMonths.add(signedMonth);
      }
    });

    // Ensure all months from original data are in monthlyData
    allMonths.forEach(month => {
      if (!monthlyData[month]) {
        monthlyData[month] = { newOps: 0, contractsSigned: 0, activeOps: 0 };
      }
    });

    // Calculate active opportunities for each month (filtered by sales rep)
    // IMPORTANT: Calculate for ALL months, not just months with newOps/contractsSigned
    const now = new Date();
    const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    Object.keys(monthlyData).forEach(month => {
      const [year, monthNum] = month.split('-').map(Number);
      const monthEnd = new Date(year, monthNum, 0, 23, 59, 59, 999); // Last day of that month
      const isCurrentMonth = month === currentMonth;

      // Count ALL filtered opportunities active in this month
      filteredRecords.forEach((record: any) => {
        if (record.CreatedDate) {
          const createdDate = new Date(record.CreatedDate);
          const wasCreatedByThen = createdDate <= monthEnd;

          if (!wasCreatedByThen) {
            return; // Not created yet in this month
          }

          // For current month: use StageName to determine if currently open
          // For historical months: use Start_Date__c to determine if it was still open then
          if (isCurrentMonth) {
            // Current month: count if currently open (StageName)
            const isCurrentlyOpen =
              record.StageName && !['Closed Won', 'Closed Lost'].includes(record.StageName);

            if (isCurrentlyOpen) {
              monthlyData[month].activeOps++;
            }
          } else {
            // Historical month: count if not started yet by that month's end
            // If Start_Date__c is null or after the month end, it was still active/open
            const startDate = record.Start_Date__c ? new Date(record.Start_Date__c) : null;
            const wasNotStartedYet = !startDate || startDate > monthEnd;

            if (wasNotStartedYet) {
              monthlyData[month].activeOps++;
            }
          }
        }
      });
    });

    // Convert to sorted array (no projections when filtered)
    displayData = Object.keys(monthlyData)
      .sort()
      .map(month => ({
        month,
        newOps: monthlyData[month].newOps,
        contractsSigned: monthlyData[month].contractsSigned,
        activeOps: monthlyData[month].activeOps,
        isProjection: false,
      }));
  }

  // Calculate max value for scaling
  const maxValue =
    displayData && displayData.length > 0
      ? Math.max(...displayData.map(d => Math.max(d.newOps, d.contractsSigned, d.activeOps)))
      : 0;
  const yAxisMax = Math.ceil(maxValue * 1.2); // 20% padding

  // Format month labels (e.g., "2025-01" -> "Jan 2025")
  const formatMonth = (monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Calculate chart dimensions based on number of data points
  const minMonthWidth = 120;
  const baseWidth = 800;
  const calculatedWidth =
    displayData && displayData.length > 0
      ? Math.max(baseWidth, displayData.length * minMonthWidth + 200)
      : baseWidth;
  const chartWidth = calculatedWidth;
  const chartHeight = 400;
  const padding = { top: 40, right: 60, bottom: 60, left: 60 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Calculate points for each line
  const calculatePoints = (values: number[]) => {
    return values.map((value, index) => {
      const x = padding.left + (index / Math.max(displayData.length - 1, 1)) * innerWidth;
      const y = padding.top + innerHeight - (value / yAxisMax) * innerHeight;

      return { x, y, value };
    });
  };

  const newOpsPoints =
    displayData && displayData.length > 0 ? calculatePoints(displayData.map(d => d.newOps)) : [];
  const contractsSignedPoints =
    displayData && displayData.length > 0
      ? calculatePoints(displayData.map(d => d.contractsSigned))
      : [];
  const activeOpsPoints =
    displayData && displayData.length > 0 ? calculatePoints(displayData.map(d => d.activeOps)) : [];

  // Handle mouse move on SVG to detect closest point
  const handleSvgMouseMove = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (!svgRef.current || newOpsPoints.length === 0) return;

      const rect = svgRef.current.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;

      // Find closest point
      let closestIndex = 0;
      let minDistance = Math.abs(mouseX - newOpsPoints[0].x);

      for (let i = 1; i < newOpsPoints.length; i++) {
        const distance = Math.abs(mouseX - newOpsPoints[i].x);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = i;
        }
      }

      // Only show tooltip if within reasonable distance (50px)
      if (minDistance < 50) {
        setHoveredPoint({
          month: displayData[closestIndex].month,
          newOps: displayData[closestIndex].newOps,
          contractsSigned: displayData[closestIndex].contractsSigned,
          activeOps: displayData[closestIndex].activeOps,
          x: newOpsPoints[closestIndex].x,
          y: newOpsPoints[closestIndex].y,
          isProjection: displayData[closestIndex].isProjection,
          isPartial: displayData[closestIndex].isPartial,
          actualData: displayData[closestIndex].actualData,
        });
      } else {
        setHoveredPoint(null);
      }
    },
    [newOpsPoints, displayData]
  );

  const handleSvgMouseLeave = useCallback(() => {
    setHoveredPoint(null);
  }, []);

  if (!displayData || displayData.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600">
        <div className="text-center">
          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No trend data available</p>
        </div>
      </div>
    );
  }

  // Find index where projections start
  const firstProjectionIndex = displayData.findIndex(d => d.isProjection);
  const hasProjections = firstProjectionIndex !== -1;

  // Split points into actual and projected segments
  const splitPoints = (points: { x: number; y: number; value: number }[]) => {
    if (!hasProjections) return { actual: points, projected: [] };

    return {
      actual: points.slice(0, firstProjectionIndex),
      projected: points.slice(firstProjectionIndex - 1), // Include last actual point for continuity
    };
  };

  const newOpsSplit = splitPoints(newOpsPoints);
  const contractsSignedSplit = splitPoints(contractsSignedPoints);
  const activeOpsSplit = splitPoints(activeOpsPoints);

  // Create path string for line
  const createPath = (points: { x: number; y: number }[]) => {
    if (points.length === 0) return '';

    return points.reduce((path, point, index) => {
      if (index === 0) {
        return `M ${point.x} ${point.y}`;
      }

      // Smooth curve using quadratic bezier
      const prevPoint = points[index - 1];
      const midX = (prevPoint.x + point.x) / 2;

      return `${path} Q ${prevPoint.x} ${prevPoint.y}, ${midX} ${(prevPoint.y + point.y) / 2} Q ${midX} ${(prevPoint.y + point.y) / 2}, ${point.x} ${point.y}`;
    }, '');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="w-full"
    >
      {/* Title */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/30 dark:to-indigo-800/20 rounded-xl shadow-md border border-indigo-200 dark:border-indigo-800/30">
          <TrendingUp className="w-6 h-6 text-indigo-600 dark:text-indigo-400" strokeWidth={2.5} />
        </div>
        <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-1.5">
          {title}
          <MetricInfoTooltip info={info} query={query} />
        </h3>
      </div>

      {/* Sales Rep Filter Dropdown */}
      {salesReps.length > 0 && (
        <motion.div
          className="mb-8 flex justify-center"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
        >
          <div className="w-80">
            <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <svg
                className="w-4 h-4 text-indigo-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              Filter by Sales Rep
            </label>
            <div className="relative group" ref={dropdownRef}>
              {/* Custom Dropdown Button */}
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full pl-11 pr-10 py-3.5 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 text-gray-900 dark:text-gray-100 font-semibold shadow-lg hover:shadow-xl focus:shadow-2xl focus:ring-4 focus:ring-indigo-500/30 focus:border-indigo-500 transition-all duration-300 cursor-pointer hover:border-indigo-400 dark:hover:border-indigo-500 text-base text-left flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  {selectedSalesRep === 'All Sales Reps' ? (
                    <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  ) : (
                    <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  )}
                  <span>{selectedSalesRep}</span>
                </div>
                <ChevronDown
                  className={`w-5 h-5 text-indigo-600 dark:text-indigo-400 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Animated border glow */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10"></div>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 w-full mt-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden"
                  >
                    {/* All Sales Reps Option */}
                    <button
                      onClick={() => {
                        setSelectedSalesRep('All Sales Reps');
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors duration-200 ${
                        selectedSalesRep === 'All Sales Reps'
                          ? 'bg-indigo-50 dark:bg-indigo-900/30'
                          : ''
                      }`}
                    >
                      <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      <span className="flex-1 text-left font-medium text-gray-900 dark:text-gray-100">
                        All Sales Reps
                      </span>
                      {selectedSalesRep === 'All Sales Reps' && (
                        <Check className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </button>

                    {/* Divider */}
                    <div className="border-t border-gray-200 dark:border-gray-700"></div>

                    {/* Individual Sales Reps */}
                    {salesReps.map(rep => (
                      <button
                        key={rep}
                        onClick={() => {
                          setSelectedSalesRep(rep);
                          setIsDropdownOpen(false);
                        }}
                        className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors duration-200 ${
                          selectedSalesRep === rep ? 'bg-indigo-50 dark:bg-indigo-900/30' : ''
                        }`}
                      >
                        <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        <span className="flex-1 text-left font-medium text-gray-900 dark:text-gray-100">
                          {rep}
                        </span>
                        {selectedSalesRep === rep && (
                          <Check className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        )}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {/* Helper text */}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-center">
              {selectedSalesRep === 'All Sales Reps'
                ? 'Viewing combined data for all sales reps'
                : `Viewing data for ${selectedSalesRep}`}
            </p>
          </div>
        </motion.div>
      )}

      {/* Chart Container with Horizontal Scroll */}
      <div className="bg-gradient-to-br from-white via-gray-50/30 to-white dark:from-gray-800 dark:via-gray-800/50 dark:to-gray-800 rounded-2xl border-2 border-gray-200 dark:border-gray-700 p-8 shadow-2xl hover:shadow-3xl transition-all duration-300">
        <div className="overflow-x-auto overflow-y-visible scrollbar-thin scrollbar-thumb-indigo-300 dark:scrollbar-thumb-indigo-600 scrollbar-track-gray-100 dark:scrollbar-track-gray-800 hover:scrollbar-thumb-indigo-400 dark:hover:scrollbar-thumb-indigo-500">
          <svg
            ref={svgRef}
            width={chartWidth}
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            className="min-w-full cursor-crosshair"
            style={{ minWidth: `${chartWidth}px` }}
            onMouseMove={handleSvgMouseMove}
            onMouseLeave={handleSvgMouseLeave}
          >
            {/* Grid lines */}
            {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
              const y = padding.top + innerHeight * (1 - ratio);

              return (
                <g key={`grid-${index}`}>
                  <line
                    x1={padding.left}
                    y1={y}
                    x2={chartWidth - padding.right}
                    y2={y}
                    stroke="currentColor"
                    strokeWidth={index === 0 ? '2' : '1'}
                    strokeDasharray={index === 0 ? 'none' : '4 4'}
                    className={
                      index === 0
                        ? 'text-gray-300 dark:text-gray-600'
                        : 'text-gray-200 dark:text-gray-700'
                    }
                  />
                  <text
                    x={padding.left - 15}
                    y={y + 5}
                    textAnchor="end"
                    className="text-sm font-semibold fill-gray-600 dark:fill-gray-400"
                  >
                    {Math.round(yAxisMax * ratio)}
                  </text>
                </g>
              );
            })}

            {/* X-axis labels */}
            {displayData.map((d, index) => {
              const x = padding.left + (index / (displayData.length - 1)) * innerWidth;

              return (
                <g key={`label-${index}`}>
                  <text
                    x={x}
                    y={chartHeight - padding.bottom + 25}
                    textAnchor="middle"
                    className={`text-sm font-semibold ${d.isProjection ? 'fill-amber-500 dark:fill-amber-400' : 'fill-gray-700 dark:fill-gray-300'}`}
                  >
                    {formatMonth(d.month)}
                  </text>
                  {d.isProjection && (
                    <text
                      x={x}
                      y={chartHeight - padding.bottom + 40}
                      textAnchor="middle"
                      className="text-xs fill-amber-500 dark:fill-amber-400"
                      style={{ fontSize: '10px' }}
                    >
                      (Proj.)
                    </text>
                  )}
                </g>
              );
            })}

            {/* Line 1: New Ops (Blue) - Actual Data */}
            <motion.path
              d={createPath(newOpsSplit.actual)}
              fill="none"
              stroke="#3B82F6"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
              className="drop-shadow-sm"
            />

            {/* Line 1: New Ops (Blue) - Projected Data */}
            {hasProjections && (
              <motion.path
                d={createPath(newOpsSplit.projected)}
                fill="none"
                stroke="#3B82F6"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="8 6"
                opacity="0.6"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.3 }}
                className="drop-shadow-sm"
              />
            )}

            {newOpsPoints.map((point, index) => (
              <g key={`new-ops-${index}`}>
                {/* Visible circle */}
                <motion.circle
                  cx={point.x}
                  cy={point.y}
                  r={hoveredPoint?.month === displayData[index].month ? 10 : 7}
                  fill={displayData[index].isProjection ? 'none' : '#3B82F6'}
                  stroke={displayData[index].isProjection ? '#3B82F6' : 'white'}
                  strokeWidth={displayData[index].isProjection ? '2' : '3'}
                  opacity={displayData[index].isProjection ? 0.6 : 1}
                  strokeDasharray={displayData[index].isProjection ? '3 2' : 'none'}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                  className="drop-shadow-md pointer-events-none"
                />
                {hoveredPoint?.month === displayData[index].month && (
                  <motion.circle
                    cx={point.x}
                    cy={point.y}
                    r="15"
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="2"
                    opacity="0.3"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </g>
            ))}

            {/* Line 2: Contracts Signed (Green) - Actual Data */}
            <motion.path
              d={createPath(contractsSignedSplit.actual)}
              fill="none"
              stroke="#10B981"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.2 }}
              className="drop-shadow-sm"
            />

            {/* Line 2: Contracts Signed (Green) - Projected Data */}
            {hasProjections && (
              <motion.path
                d={createPath(contractsSignedSplit.projected)}
                fill="none"
                stroke="#10B981"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="8 6"
                opacity="0.6"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.5 }}
                className="drop-shadow-sm"
              />
            )}

            {contractsSignedPoints.map((point, index) => (
              <g key={`contracts-${index}`}>
                {/* Visible circle */}
                <motion.circle
                  cx={point.x}
                  cy={point.y}
                  r={hoveredPoint?.month === displayData[index].month ? 10 : 7}
                  fill={displayData[index].isProjection ? 'none' : '#10B981'}
                  stroke={displayData[index].isProjection ? '#10B981' : 'white'}
                  strokeWidth={displayData[index].isProjection ? '2' : '3'}
                  opacity={displayData[index].isProjection ? 0.6 : 1}
                  strokeDasharray={displayData[index].isProjection ? '3 2' : 'none'}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2 + index * 0.1, duration: 0.3 }}
                  className="drop-shadow-md pointer-events-none"
                />
                {hoveredPoint?.month === displayData[index].month && (
                  <motion.circle
                    cx={point.x}
                    cy={point.y}
                    r="15"
                    fill="none"
                    stroke="#10B981"
                    strokeWidth="2"
                    opacity="0.3"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </g>
            ))}

            {/* Line 3: Active Opps (Orange) - Actual Data */}
            <motion.path
              d={createPath(activeOpsSplit.actual)}
              fill="none"
              stroke="#F97316"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.4 }}
              className="drop-shadow-sm"
            />

            {/* Line 3: Active Opps (Orange) - Projected Data */}
            {hasProjections && (
              <motion.path
                d={createPath(activeOpsSplit.projected)}
                fill="none"
                stroke="#F97316"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="8 6"
                opacity="0.6"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.7 }}
                className="drop-shadow-sm"
              />
            )}

            {activeOpsPoints.map((point, index) => (
              <g key={`active-ops-${index}`}>
                {/* Visible circle */}
                <motion.circle
                  cx={point.x}
                  cy={point.y}
                  r={hoveredPoint?.month === displayData[index].month ? 10 : 7}
                  fill={displayData[index].isProjection ? 'none' : '#F97316'}
                  stroke={displayData[index].isProjection ? '#F97316' : 'white'}
                  strokeWidth={displayData[index].isProjection ? '2' : '3'}
                  opacity={displayData[index].isProjection ? 0.6 : 1}
                  strokeDasharray={displayData[index].isProjection ? '3 2' : 'none'}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.4 + index * 0.1, duration: 0.3 }}
                  className="drop-shadow-md pointer-events-none"
                />
                {hoveredPoint?.month === displayData[index].month && (
                  <motion.circle
                    cx={point.x}
                    cy={point.y}
                    r="15"
                    fill="none"
                    stroke="#F97316"
                    strokeWidth="2"
                    opacity="0.3"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                )}
              </g>
            ))}

            {/* Enhanced Tooltip */}
            <AnimatePresence>
              {hoveredPoint &&
                (() => {
                  // Calculate tooltip position to prevent cutoff
                  const tooltipWidth = 180;
                  const tooltipHeight = hoveredPoint.isProjection ? 105 : 95;

                  // Check if tooltip would overflow on the right
                  let tooltipX = hoveredPoint.x - tooltipWidth / 2;

                  if (tooltipX + tooltipWidth > chartWidth - padding.right) {
                    tooltipX = chartWidth - padding.right - tooltipWidth - 10;
                  }

                  // Check if tooltip would overflow on the left
                  if (tooltipX < padding.left) {
                    tooltipX = padding.left + 10;
                  }

                  const tooltipY = hoveredPoint.y - (hoveredPoint.isProjection ? 95 : 85);

                  return (
                    <motion.g
                      initial={{ opacity: 0, scale: 0.85, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.85, y: 10 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                      {/* Tooltip Background */}
                      <rect
                        x={tooltipX}
                        y={tooltipY}
                        width={tooltipWidth}
                        height={tooltipHeight}
                        rx="12"
                        fill="rgba(17, 24, 39, 0.95)"
                        stroke="rgba(99, 102, 241, 0.6)"
                        strokeWidth="2"
                        className="drop-shadow-2xl"
                      />

                      {/* Month Label */}
                      <text
                        x={tooltipX + tooltipWidth / 2}
                        y={tooltipY + 20}
                        textAnchor="middle"
                        className="fill-white text-sm font-bold"
                      >
                        {formatMonth(hoveredPoint.month)}
                      </text>

                      {/* Divider Line */}
                      <line
                        x1={tooltipX + 15}
                        y1={tooltipY + 28}
                        x2={tooltipX + tooltipWidth - 15}
                        y2={tooltipY + 28}
                        stroke={
                          hoveredPoint.isProjection
                            ? 'rgba(251, 191, 36, 0.4)'
                            : 'rgba(99, 102, 241, 0.3)'
                        }
                        strokeWidth="1.5"
                      />

                      {/* New Ops Row */}
                      <g>
                        <circle
                          cx={tooltipX + 15}
                          cy={tooltipY + 43}
                          r="5"
                          fill="#3B82F6"
                          stroke="rgba(59, 130, 246, 0.4)"
                          strokeWidth="2"
                        />
                        <text
                          x={tooltipX + 28}
                          y={tooltipY + 47}
                          className="fill-gray-300 text-xs font-medium"
                        >
                          New Ops
                        </text>
                        <text
                          x={tooltipX + tooltipWidth - 10}
                          y={tooltipY + 47}
                          textAnchor="end"
                          className={
                            hoveredPoint.isProjection
                              ? 'fill-amber-300 text-sm font-black'
                              : 'fill-blue-400 text-sm font-black'
                          }
                        >
                          {hoveredPoint.newOps}
                          {hoveredPoint.isProjection && ' ~'}
                        </text>
                      </g>

                      {/* Contracts Signed Row */}
                      <g>
                        <circle
                          cx={tooltipX + 15}
                          cy={tooltipY + 63}
                          r="5"
                          fill="#10B981"
                          stroke="rgba(16, 185, 129, 0.4)"
                          strokeWidth="2"
                        />
                        <text
                          x={tooltipX + 28}
                          y={tooltipY + 67}
                          className="fill-gray-300 text-xs font-medium"
                        >
                          Contracts
                        </text>
                        <text
                          x={tooltipX + tooltipWidth - 10}
                          y={tooltipY + 67}
                          textAnchor="end"
                          className={
                            hoveredPoint.isProjection
                              ? 'fill-amber-300 text-sm font-black'
                              : 'fill-green-400 text-sm font-black'
                          }
                        >
                          {hoveredPoint.contractsSigned}
                          {hoveredPoint.isProjection && ' ~'}
                        </text>
                      </g>

                      {/* Active Opps Row */}
                      <g>
                        <circle
                          cx={tooltipX + 15}
                          cy={tooltipY + 83}
                          r="5"
                          fill="#F97316"
                          stroke="rgba(249, 115, 22, 0.4)"
                          strokeWidth="2"
                        />
                        <text
                          x={tooltipX + 28}
                          y={tooltipY + 87}
                          className="fill-gray-300 text-xs font-medium"
                        >
                          Active Opps
                        </text>
                        <text
                          x={tooltipX + tooltipWidth - 10}
                          y={tooltipY + 87}
                          textAnchor="end"
                          className={
                            hoveredPoint.isProjection
                              ? 'fill-amber-300 text-sm font-black'
                              : 'fill-orange-400 text-sm font-black'
                          }
                        >
                          {hoveredPoint.activeOps}
                          {hoveredPoint.isProjection && ' ~'}
                        </text>
                      </g>

                      {/* Projection Note */}
                      {hoveredPoint.isProjection && (
                        <text
                          x={tooltipX + tooltipWidth / 2}
                          y={tooltipY + tooltipHeight - 8}
                          textAnchor="middle"
                          className="fill-amber-300 text-xs italic font-medium"
                        >
                          Projected
                        </text>
                      )}
                    </motion.g>
                  );
                })()}
            </AnimatePresence>
          </svg>
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center flex-wrap gap-4 mt-8">
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/30">
            <div className="w-5 h-5 rounded-full bg-blue-500 shadow-md"></div>
            <span className="text-sm font-bold text-blue-700 dark:text-blue-300">
              New Sales Ops Created
            </span>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/30">
            <div className="w-5 h-5 rounded-full bg-green-500 shadow-md"></div>
            <span className="text-sm font-bold text-green-700 dark:text-green-300">
              Contracts Signed
            </span>
          </div>
          <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800/30">
            <div className="w-5 h-5 rounded-full bg-orange-500 shadow-md"></div>
            <span className="text-sm font-bold text-orange-700 dark:text-orange-300">
              Active Opps
            </span>
          </div>

          {/* Projection Legend */}
          {hasProjections && (
            <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border-2 border-dashed border-amber-300 dark:border-amber-700/50">
              <svg width="24" height="24" viewBox="0 0 24 24" className="flex-shrink-0">
                <line
                  x1="2"
                  y1="12"
                  x2="22"
                  y2="12"
                  stroke="#F59E0B"
                  strokeWidth="3"
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                />
              </svg>
              <span className="text-sm font-bold text-amber-700 dark:text-amber-300">
                Projected
              </span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};
